import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import {
  get,
  isNaN,
  noop,
  omit,
  sumBy,
  isEmpty,
  sortBy,
  compact,
} from 'lodash';
import { connect } from 'react-redux';
import Calendar from 'react-calendar';
import {
  compose,
  setDisplayName,
} from 'recompose';
import {
  Col,
  Row,
} from 'reactstrap';
import uuidv1 from 'uuid/v1';
import Button from '@material-ui/core/Button';
import { grey700 } from '@material-ui/core/colors';
import IconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import ContentAdd from '@material-ui/icons/Add';
import Delete from '@material-ui/icons/Delete';
import CheckBox from '@material-ui/core/Checkbox';

import { isMobile } from 'helpers/detect-mobile';
import Serializer from 'helpers/form-serialize';
import {
  appendDollar,
  dispatchRestMaker,
  decimalFixed,
  getPickedAvailableDate,
} from 'helpers/utils';
import immutableToJS from 'helpers/immutableToJS';
import {
  ConfirmAction,
  injectIntl,
  QuickHelp,
  SmartDialog,
  Tags,
} from 'components';
import SelectField from 'components/SmartForm/components/SelectField';
import goodActions from 'redux/goods/actions';
import { selectState } from 'redux/selectors';
import storeActions from 'redux/storeItems/actions';
import { setHotNotification } from 'redux/ui/actions';
import AutoFill from './AutoFill';
import moment from 'moment';

const addBtnBg = '#ffd24c';
const addBtnBgHover = '#3685DC';

const styles = theme => ({
  addBtn: {
    backgroundColor: addBtnBg,
    '&:hover': {
      backgroundColor: addBtnBgHover,
    },
  },
  addBtnLabel: { color: '#fff' },
  bulkLabel: {
    color: addBtnBg,
  },
  saveBtn: {
    backgroundColor: '#4caf50',
    color: 'white',
    '&:hover': {
      backgroundColor: '#3c9f40',
    },
  },
  removeBtn: {
    color: 'rgba(0, 0, 0, 0.54)',
    marginLeft: 20,
  },
  saveAndAddPackoutBtn: {
    backgroundColor: addBtnBg,
  },
  saveAndAddPackoutBtnLabel: {
    color: '#313131',
    fontSize: 14,
    fontWeight: 500,
  },
  inputField: {
    fontSize: 20,
    fontWeight: 400,
    padding: 5,
  },
  label: { transform: 'translate(14px, 7px) scale(1);' },
  selectItem: { fontSize: 12 },
  selectLabel: {
    [theme.breakpoints.down('xs')]: {
      font: 9,
    },
    fontSize: 12,
  },
  selectMenu: {
    [theme.breakpoints.down('xs')]: {
      paddingRight: 17,
    },
    paddingBottom: 5,
    paddingTop: 5,
  },
  selectRoot: { marginBottom: 22, width: '100%' },
  switch: {
    color: addBtnBg,
  },
});

const getStateFromInfo = (props) => {
  const { info } = props;
  const availableQuantity = info.availableQuantity || 0;
  const isBulk = props.isEdit ? (get(info, 'packrows.0.isBulk') || false) : (props.itemType === 'bulk-qty');
  const packrows = (info.packrows || [{
    availableQuantity: info.availableQuantity || 0,
    cost: 0,
    count: 1,
    pack: info.pack,
    price: 0,
    uom: info.uom,
  }]).map(packrow => ({
    ...packrow,
    id: packrow.id || uuidv1(),
    deleted: false,
    originalValues: { count: packrow.count, uom: packrow.uom, pack: packrow.pack },
  }));
  const pack = get(packrows, '0.pack');
  const uom = get(packrows, '0.uom');
  const newPackrows = packrows.map(packrow => ({
    ...packrow,
    availableQuantity: (availableQuantity / packrow.count).toFixed(0),
    isBulk,
    uom,
    pack,
  })).slice(0, 1);
  const customPackrows = packrows.map(packrow => ({
    ...packrow,
    isBulk,
    pack,
    uom,
  }));
  while (customPackrows.length < 2) {
    customPackrows.push({
      ...customPackrows[0],
      id: uuidv1(),
      count: '',
      isBulk,
      pack,
      uom,
      cost: 0,
      price: 0,
    });
  }
  return {
    availableQuantity,
    description: info.description || '',
    pack: info.pack,
    category: get(info, 'productData.category') || '',
    name: get(info, 'productData.name') || '',
    subname: get(info, 'productData.subname') || '',
    isBulk,
    packrows: props.isEdit ? packrows : (isBulk ? customPackrows.slice(0, 2) : newPackrows),
    tags: (info.tags || []).slice(),
    uom: info.uom,
    autoFillKey: uuidv1(),
    pickedAvailableDates: info.availableDates || [],
  };
};

class EditItemDialog extends Component {
  static contextTypes = {
    isFarmer: PropTypes.bool,
    isDistributor: PropTypes.bool,
  };

  static propTypes = {
    classes: PropTypes.shape({}).isRequired,
    contactId: PropTypes.number,
    goodRequesting: PropTypes.bool.isRequired,
    info: PropTypes.shape({
      availableQuantity: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
      ]),
      pack: PropTypes.string,
      packrows: PropTypes.arrayOf(PropTypes.shape({
        count: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]),
        cost: PropTypes.number,
      })),
      productData: PropTypes.shape({
        category: PropTypes.string,
        name: PropTypes.string,
        subname: PropTypes.string,
      }),
      uom: PropTypes.string,
    }),
    isEdit: PropTypes.bool,
    open: PropTypes.bool.isRequired,
    packs: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
      }),
    ).isRequired,
    storeItemRequesting: PropTypes.bool.isRequired,
    uoms: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
      }),
    ).isRequired,

    createGood: PropTypes.func.isRequired,
    createStoreItem: PropTypes.func.isRequired,
    formatMessage: PropTypes.func.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    removeStoreItem: PropTypes.func.isRequired,
    removeGood: PropTypes.func.isRequired,
    reloadStore: PropTypes.func.isRequired,
    setHotNotification: PropTypes.func.isRequired,
    updateStateData: PropTypes.func,
    globals: PropTypes.arrayOf(PropTypes.shape({})),
    isRetailPage: PropTypes.bool,
  };

  static defaultProps = {
    contactId: null,
    info: {},
    isEdit: false,
    updateStateData: noop,
    globals: [],
    isRetailPage: false,
  };

  state = {
    ...getStateFromInfo(this.props),
    isEdit: this.props.isEdit,
    focusedPrice: {},
    focusedCount: {},
    focusedAvailableQuantity: {},
    focusedBulkAvailableQuantity: {},
    touched: false,
  };

  /* componentWillReceiveProps({ info }) {
    if (info !== this.props.info) {
      this.setState(getStateFromInfo({
        ...this.state.info,
        ...info,
      }));
    }
  } */

  onChangeAvailableQuantity = (value, index) => {
    const { packrows } = this.state;
    const newPackrows = packrows.slice();
    newPackrows[index].availableQuantity = Math.trunc(value);
    this.setState({
      packrows: newPackrows,
      touched: true,
    }, this.onUpdate);
  }

  onChangeBulkModeAvailableQuantity = (value) => {
    this.setState({ availableQuantity: value ? Number(parseFloat(value).toFixed(2)) : 0, touched: true }, this.onUpdate);
  }

  onChangePackRow = (index, name, value) => {
    const { packrows } = this.state;
    const newPackrows = packrows.slice();
    newPackrows[index] = {
      ...newPackrows[index],
      [name]: Number(value),
    };
    this.setState({ packrows: newPackrows, touched: true }, this.onUpdate);
  }

  onChangePackUOM = (index, name, value) => {
    const { packrows } = this.state;
    const newPackrows = packrows.slice();

    newPackrows[index] = {
      ...newPackrows[index],
      [name]: value,
    };

    this.setState({ packrows: newPackrows, touched: true });
  }

  onChangeTags = (tags) => {
    const sortedTags = sortBy(tags || [], [String], ['asc']);
    this.setState({ tags: sortedTags, touched: true });
  }

  onChangeUOM = (index, value) => {
    const { globals } = this.props;
    const { isBulk, packrows } = this.state;
    let newPackrows = packrows.slice();

    if (isBulk) {
      newPackrows = newPackrows.map(packrow => ({
        ...packrow,
        count: decimalFixed(packrow.count),
        uom: value,
      }));
    } else {
      newPackrows[index] = {
        ...newPackrows[index],
        uom: value,
      };
    }

    this.setState({ packrows: newPackrows, touched: true }, this.onUpdate);
  }

  onUpdate = () => {
    const { availableQuantity, isBulk, packrows } = this.state;
    if (isBulk) {
      const newPackrows = packrows.slice().map(item => ({
        ...item,
        availableQuantity: Math.trunc(availableQuantity / decimalFixed(item.count)),
      }));
      this.setState({ packrows: newPackrows });
    } else {
      this.setState({
        availableQuantity: sumBy(packrows, row =>
          parseFloat(row.availableQuantity) * decimalFixed(row.count)),
      });
    }
  }

  onSelect = (item) => {
    this.setState({ ...item, touched: true });
  }

  setFocusedBulkAvailableQuantity = (rowId, focused, availableQuantity) => {
    if (focused) {
      this.setState({
        focusedBulkAvailableQuantity: {
          ...this.state.focusedBulkAvailableQuantity,
          [rowId]: {
            availableQuantity,
            focused,
          },
        },
      });
    } else {
      this.setState({
        focusedBulkAvailableQuantity: {
          ...this.state.focusedBulkAvailableQuantity,
          [rowId]: undefined,
        },
      });
    }
  };

  setFocusedAvailableQuantity = (rowId, focused, availableQuantity) => {
    if (focused) {
      this.setState({
        focusedAvailableQuantity: {
          ...this.state.focusedAvailableQuantity,
          [rowId]: {
            availableQuantity,
            focused,
          },
        },
      });
    } else {
      this.setState({
        focusedAvailableQuantity: {
          ...this.state.focusedAvailableQuantity,
          [rowId]: undefined,
        },
      });
    }
  };

  setFocusedCount = (rowId, focused, count) => {
    if (focused) {
      this.setState({
        focusedCount: {
          ...this.state.focusedCount,
          [rowId]: {
            count,
            focused,
          },
        },
      });
    } else {
      this.setState({
        focusedCount: {
          ...this.state.focusedCount,
          [rowId]: undefined,
        },
      });
    }
  };

  setFocusedPrice = (rowId, focused, price) => {
    if (focused) {
      this.setState({
        focusedPrice: {
          ...this.state.focusedPrice,
          [rowId]: {
            price,
            focused,
          },
        },
      });
    } else {
      this.setState({
        focusedPrice: {
          ...this.state.focusedPrice,
          [rowId]: undefined,
        },
      });
    }
  };

  addPackRow = () => {
    const { packrows, isBulk } = this.state;
    const newPackrows = packrows.slice();
    const id = uuidv1();
    if (isBulk) {
      newPackrows.push({
        availableQuantity: 0,
        cost: 0,
        count: 1,
        id,
        isBulk,
        price: 0,
        pack: '',
        uom: packrows[0].uom,
      });
    } else {
      newPackrows.push({
        availableQuantity: 0,
        cost: 0,
        count: 1,
        id,
        isBulk,
        price: 0,
        pack: '',
      });
    }

    this.setState({ packrows: newPackrows, touched: true });
  }

  removePackRow = (index) => {
    const { packrows } = this.state;
    // const rowToDelete = packrows[index];
    // rowToDelete.deleted = true;
    packrows[index].deleted = true;

    this.setState({
      packrows,
      touched: true,
    });
  }

  handlePickDeliveryDate = (value) => {
    const { pickedAvailableDates } = this.state;
    if (value) {
      const selectedDate = moment(value).format('YYYY-MM-DD');
      const hasPickedDate = pickedAvailableDates.filter(x => x === selectedDate).length > 0;
      if (!hasPickedDate) {
        pickedAvailableDates.push(selectedDate);
      } else {
        for (let i = 0; i < pickedAvailableDates.length; i++) {
          if (pickedAvailableDates[i] === selectedDate) {
            pickedAvailableDates.splice(i, 1);
          }
        }
      }

      // remove picked available dates in the past
      const updatedAvailableDates = [];
      for (let i = 0; i < pickedAvailableDates.length; i++) {
        if ((moment(pickedAvailableDates[i]).isAfter(moment(new Date()).add(-1, 'days')))) {
          updatedAvailableDates.push(pickedAvailableDates[i]);
        }
      }

      const sortedAvailableDates = sortBy(updatedAvailableDates, [String], ['asc']);
      this.setState({ pickedAvailableDates: sortedAvailableDates, touched: true });
    }
  }

  render() {
    const {
      classes,
      contactId,
      createGood,
      createStoreItem,
      formatMessage,
      goodRequesting,
      info,
      onRequestClose,
      open,
      packs,
      removeStoreItem,
      removeGood,
      reloadStore,
      setHotNotification,
      storeItemRequesting,
      uoms,
      updateStateData,
      globals,
      isRetailPage,
    } = this.props;
    const { isFarmer } = this.context;
    const {
      autoFillKey,
      availableQuantity,
      isBulk,
      isEdit,
      focusedPrice,
      focusedCount,
      focusedAvailableQuantity,
      focusedBulkAvailableQuantity,
      packrows,
      tags,
      description,
      category,
      name,
      subname,
      touched,
      pickedAvailableDates,
    } = this.state;
    const requesting = isFarmer ? storeItemRequesting : goodRequesting;
    const invalid = value => isNaN(value) || value < 0 || value === '';
    const saveButtonDisabled =
      !touched ||
      requesting ||
      !name ||
      !subname ||
      invalid(availableQuantity);
    const actions = (
      <div className="actions">
        <Button
          color="primary"
          className={classes.saveBtn}
          disabled={saveButtonDisabled}
          onClick={() => { this.saveAndContinue = false; }}
          type="submit"
          variant="contained"
        >
          {isMobile ? formatMessage('Save') : formatMessage('Save & Exit')}
        </Button>
        {!isBulk && !isEdit && (
          <Fragment>
            <Button
              color="primary"
              classes={{
                root: classes.saveAndAddPackoutBtn,
                label: classes.saveAndAddPackoutBtnLabel,
              }}
              disabled={saveButtonDisabled}
              onClick={() => { this.saveAndContinue = true; }}
              type="submit"
              variant="contained"
              style={{ marginLeft: 5 }}
            >
              {formatMessage('Save & Add Another Pack-out')}
            </Button>
            <QuickHelp placement="top-end" style={{ marginLeft: 10 }}>
              {formatMessage('TO ADD PACK-OUTS TO THIS ITEM Click SAVE & ADD PACK-OUT')}
            </QuickHelp>
          </Fragment>
        )}
      </div>
    );
    const uomsList = uoms.map(uom => ({
      label: uom.name,
      value: uom.name,
    }));
    uomsList.unshift({
      label: '',
      value: '',
    });
    const packsList = packs.map(pack => ({
      label: pack.name,
      value: pack.name,
    }));
    packsList.unshift({
      label: '',
      value: '',
    });
    if (!open) return null;
    return (
      <SmartDialog
        maxHeight={500}
        onClose={onRequestClose}
        open={open}
        title={formatMessage(isEdit ? `Edit ${isBulk ? 'Bulk Qty' : 'Pre-Packed'} Item` : `Add ${isBulk ? 'Bulk Qty' : 'Pre-Packed'} Item`)}
        closeButtonOnTop
        noOutsideClick
      >
        { isBulk && (
          <div style={{ position: 'absolute', top: '27px', left: '185px' }}>
            <QuickHelp style={{ marginLeft: 10 }}>
              {formatMessage('Bulk shows available pack(s) from a total weight or count')}
            </QuickHelp>
          </div>
          )
        }
        <div style={!isMobile ? { position: 'absolute', top: '20px', right: '65px' } : {}}>
          {
            name && (
              !category ? (
                <div style={{ fontSize: 12, lineHeight: !isMobile ? '40px' : '20px', textAlign: !isMobile ? 'right' : 'left' }}>
                  <span>{formatMessage('Create A ')}</span>
                  <span><b>{formatMessage('Custom Item')}{'. '}</b></span>
                  <QuickHelp style={{ marginLeft: 10 }}>
                    {formatMessage('Custom Item should be approved by administrator to be sold.')}
                  </QuickHelp>
                </div>
              ) : (
                <div style={{ fontSize: 12, lineHeight: '40px', textAlign: 'right' }}>
                  <span style={{ color: 'gray' }}>{formatMessage('Category')}</span>
                  {' '}{category}
                </div>
              )
            )
          }
        </div>
        <form
          className="smart-form"
          onSubmit={(e) => {
            const { packrows, tags, isBulk, category, name, subname, description, pickedAvailableDates } = this.state;
            const { isEdit } = this.state;

            e.preventDefault();

            let notDeletedPackrows = packrows.filter(packrow => !packrow.deleted);
            
            if (notDeletedPackrows.length === 2) {
              const firstPackrow = notDeletedPackrows[0];
              const lastPackrow = notDeletedPackrows[1];
              if (!firstPackrow.count || firstPackrow.count <= 0) {
                setHotNotification('Count must be greater than 0.');
                return;
              }
              if (!firstPackrow.uom && !firstPackrow.pack) {
                setHotNotification('UOM and Packs are required.');
                return;
              }
              if (!firstPackrow.uom) {
                setHotNotification('UOM required.');
                return;
              }
              if (!firstPackrow.pack) {
                setHotNotification('Packs required.');
                return;
              }
              if (!lastPackrow.count && !lastPackrow.price && !lastPackrow.pack) {
                notDeletedPackrows = [firstPackrow];
              } else {
                if (!lastPackrow.count || lastPackrow.count <= 0) {
                  setHotNotification('Count must be greater than 0.');
                  return;
                }
                if (!lastPackrow.uom && !lastPackrow.pack) {
                  setHotNotification('UOM and Packs are required.');
                  return;
                }
                if (!lastPackrow.uom) {
                  setHotNotification('UOM required.');
                  return;
                }
                if (!lastPackrow.pack) {
                  setHotNotification('Packs required.');
                  return;
                }
              }
            } else {
              let invalidPackRow = notDeletedPackrows.find(packrow => !packrow.count || packrow.count <= 0);
              if (invalidPackRow) {
                setHotNotification('Count must be greater than 0.');
                return;
              }
              invalidPackRow = notDeletedPackrows.find(packrow => !packrow.uom && !packrow.pack);
              if (invalidPackRow) {
                setHotNotification('UOM and Packs are required.');
                return;
              }
              invalidPackRow = notDeletedPackrows.find(packrow => !packrow.uom);
              if (invalidPackRow) {
                setHotNotification('UOM required.');
                return;
              }
              invalidPackRow = notDeletedPackrows.find(packrow => !packrow.pack);
              if (invalidPackRow) {
                setHotNotification('Packs required.');
                return;
              }

              if (notDeletedPackrows.length > 2) {
                const helper = {};
                const result = notDeletedPackrows.reduce((r, o) => {
                  const key = `${o.pack}-${o.count}`;
                  if (!helper[key]) {
                    helper[key] = Object.assign({}, o); // create a copy of o
                    helper[key].duplicate = 1;
                    r.push(helper[key]);
                  } else {
                    helper[key].duplicate += 1;
                  }

                  return r;
                }, []);
                invalidPackRow = result.filter(x => x.duplicate > 1).length > 0;
                if (invalidPackRow) {
                  setHotNotification('Can\'t add multiple same pack-outs');
                  return;
                }
              }
            }
            let data = {
              name: name || '',
              subname: subname || '',
              category: category || '',
              description: description || '',
              ...Serializer.serialize(e.target, { hash: true, empty: true, disabled: true }),
              packrows: isEdit ? packrows.map(packrow => ({
                ...packrow,
                availableQuantity: Math.trunc(packrow.availableQuantity),
                count: decimalFixed(packrow.count),
                cost: isFarmer ? 0 :
                  packrow.cost * (100 / (100 - (info.discount || 0))) || 0,
                isBulk,
                deleted: packrow.deleted,
                pack: packrow.pack,
                price: packrow.price || 0,
                uom: packrow.uom,
              })) : notDeletedPackrows.map(packrow => ({
                ...packrow,
                availableQuantity: Math.trunc(packrow.availableQuantity),
                count: decimalFixed(packrow.count),
                cost: isFarmer ? 0 :
                  packrow.cost * (100 / (100 - (info.discount || 0))) || 0,
                isBulk,
                pack: packrow.pack,
                price: packrow.price || 0,
                uom: packrow.uom,
              })),
              tags,
              isBulk,
              isConsumer: isRetailPage,
              availableDates: pickedAvailableDates && pickedAvailableDates.length ? pickedAvailableDates : null,
            };

            if (data.availableQuantity === '') {
              data.availableQuantity = 0;
            } else {
              data.availableQuantity = decimalFixed(data.availableQuantity);
            }

            let payload = {};
            if (isEdit) {
              data = omit(data, isBulk ? ['search', 'pack', 'uom'] : ['search', 'pack', 'uom', 'availableQuantity']);
              payload = {
                ...data,
                onSuccess: onRequestClose,
              };
            } else {
              data = omit(data, ['search']);
              const firstPack = get(data, 'pack.0');
              const firstUOM = get(data, 'uom.0');
              data.pack = firstPack;
              data.uom = firstUOM;
            }
            const onSuccess = () => {
              if (this.saveAndContinue) {
                this.setState({
                  packrows: [{
                    ...this.state.packrows[0],
                    availableQuantity: 0,
                    cost: 0,
                    count: 1,
                    price: 0,
                  }],
                  isEdit: false,
                });
                // if (!isEdit) reloadStore();
              } else {
                onRequestClose();
                // reloadStore();
              }
            };
            if (isEdit) {
              payload = {
                ...data,
                sellerAccountId: contactId,
              };
              updateStateData(payload, info.id);
            } else if (isFarmer) {
              payload = {
                ...data,
                sellerAccountId: contactId,
                onSuccess,
              };
              createStoreItem(payload);
            } else {
              payload = {
                ...data,
                sellerAccountId: contactId,
                onSuccess,
              };
              createGood(payload);
            }
          }}
        >
          <Row className="content">
            <Col className="form-field" xs="12" style={{ justifyContent: 'flex-start' }}>
              <AutoFill
                onSelect={this.onSelect}
                key={autoFillKey}
                name={name}
                primary
                disabled={isEdit}
                inputFieldStyle={{ color: name && !category ? 'red' : 'black' }}
              />
            </Col>
            <Col className="form-field" sm="6" xs="12" style={{ justifyContent: 'flex-start' }}>
              <AutoFill
                disabled={isEdit || !name}
                onSelect={this.onSelect}
                key={autoFillKey}
                name={name}
                subname={subname}
                primary={false}
                inputFieldStyle={{ color: name && subname && !category ? 'red' : 'black' }}
              />
            </Col>
            <Col
              sm="6"
              xs="12"
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'space-between',
              }}
            >
              <Tags
                defaultTags={tags}
                onChange={this.onChangeTags}
                removable={true}
              />
            </Col>
            <Col className="pack-count-rows" xs="12">
              {/* <Row style={{ position: 'relative' }}>
                <Col xs="12">
                  <Row>
                    {
                      isBulk && (
                        <Col xs="7" md="5">
                          <span className="total-uom" style={{ fontWeight: 600 }}>
                            {formatMessage('Total Bulk Qty Available')}
                          </span>
                          <TextField
                            autoComplete="off"
                            autoFocus
                            value={parseInt(availableQuantity, 10)}
                            fullWidth
                            inputProps={{ step: 'any' }}
                            min={0}
                            name="availableQuantity"
                            onChange={({ target: { value } }) => {
                              if (parseFloat(value || 0) < 0) {
                                setHotNotification('Should not be less than 0.');
                              }
                              this.onChangeBulkModeAvailableQuantity(value);
                            }}
                            placeholder="0"
                            type="number"
                            varaint="outlined"
                          />
                        </Col>
                      )
                    }
                    <Col xs={{ size: 4, offset: 1 }} md="4">
                      <span className="total-uom">
                        {isBulk ? formatMessage('UOM *') : ''}
                      </span>
                      {isBulk && (
                        <SelectField
                          classes={{
                            item: { root: classes.selectItem },
                            root: { root: classes.selectRoot },
                            select: {
                              select: classes.selectLabel,
                              selectMenu: classes.selectMenu,
                            },
                          }}
                          fullWidth
                          name="uom"
                          nativeSelect
                          onChange={e => this.onChangeUOM(0, e)}
                          options={uomsList}
                          required
                          value={packrows[0].uom || info.uom}
                        />
                      )}
                    </Col>
                  </Row>
                </Col>
              </Row> */}
              <Row>
                <Col xs={isBulk ? '11' : '12'}>
                  <Row>
                    <Col xs="2">
                      <span className="qty">
                        {isBulk ? formatMessage('Qty') : formatMessage('Qty')}
                      </span>
                    </Col>
                    {isBulk && (
                      <Col xs="2">
                        <span className="available">
                          {isBulk ? formatMessage('Packs') : ''}
                        </span>
                      </Col>
                    )}
                    <Col xs={isBulk ? 2 : 2}>
                      <span className="count">{formatMessage('Count')}</span>
                    </Col>
                    <Col xs={isBulk ? 2 : 3}>{isBulk ? formatMessage('UOM *') : formatMessage('UOM *')}</Col>
                    <Col xs={isBulk ? 2 : 3}>{formatMessage('Pack *')}</Col>
                    <Col xs={isBulk ? 2 : 2}>{appendDollar(formatMessage('Price'))}</Col>
                  </Row>
                </Col>
                {isBulk && <Col xs="1" />}
              </Row>
              {
                packrows.map((item, index) => {
                  return !item.deleted ? (
                    <Row key={item.id}>
                      <Col xs={isBulk ? '11' : '12'}>
                        <Row>
                          <Col xs={isBulk ? 2 : 2}>
                            {isBulk ? (
                              <div>
                                {index === 0 && (
                                  <TextField
                                    autoComplete="off"
                                    autoFocus
                                    value={focusedBulkAvailableQuantity[0]
                                      ? focusedBulkAvailableQuantity[0].availableQuantity > 0 ? decimalFixed(focusedBulkAvailableQuantity[0].availableQuantity) : focusedBulkAvailableQuantity[0].availableQuantity
                                      : decimalFixed(availableQuantity)}
                                    fullWidth
                                    inputProps={{ step: 'any' }}
                                    min={0}
                                    name="availableQuantity"
                                    onFocus={(e) => {
                                      if (parseFloat(e.target.value, 0) === 0) {
                                        this.onChangeBulkModeAvailableQuantity('');
                                        this.setFocusedBulkAvailableQuantity(0, true, '');
                                      }
                                    }}
                                    onBlur={() => {
                                      if (!packrows[0].availableQuantity) {
                                        this.onChangeBulkModeAvailableQuantity('0');
                                      }
                                      this.setFocusedBulkAvailableQuantity(0, false);
                                    }}
                                    onChange={(e) => {
                                      if (parseFloat(e.target.value || 0) < 0) {
                                        setHotNotification('Should not be less than 0.');
                                        e.target.value = '';
                                      }
                                      e.target.value = decimalFixed(e.target.value);
                                      this.onChangeBulkModeAvailableQuantity(e.target.value);
                                      this.setFocusedBulkAvailableQuantity(0, true, e.target.value);
                                    }}
                                    placeholder="0"
                                    type="number"
                                    varaint="outlined"
                                  />
                                )}
                              </div>
                            ) : (
                              <TextField
                                autoComplete="off"
                                autoFocus
                                value={focusedAvailableQuantity[0]
                                  ? focusedAvailableQuantity[0].availableQuantity > 0 ? Math.trunc(focusedAvailableQuantity[0].availableQuantity) : focusedAvailableQuantity[0].availableQuantity
                                  : Math.trunc(packrows[0].availableQuantity)}
                                fullWidth
                                inputProps={{ step: 'any' }}
                                min={0}
                                name="availableQuantity"
                                onFocus={(e) => {
                                  if (Number(e.target.value) === 0) {
                                    this.onChangeAvailableQuantity('', 0);
                                    this.setFocusedAvailableQuantity(0, true, '');
                                  }
                                }}
                                onBlur={() => {
                                  if (!packrows[0].availableQuantity) {
                                    this.onChangeAvailableQuantity('0', 0);
                                  }
                                  this.setFocusedAvailableQuantity(0, false);
                                }}
                                onChange={(e) => {
                                  if (parseFloat(e.target.value || 0) < 0) {
                                    setHotNotification('Should not be less than 0.');
                                    e.target.value = '';
                                  }
                                  e.target.value = Math.trunc(e.target.value);
                                  this.onChangeAvailableQuantity(e.target.value, 0);
                                  this.setFocusedAvailableQuantity(0, true, e.target.value);
                                }}
                                placeholder="0"
                                type="number"
                                varaint="outlined"
                              />
                            )}
                          </Col>
                          {isBulk && (
                            <Col xs="2">
                              <div style={{ marginBottom: 9 }}>
                                {item.count > 0
                                  ? Math.trunc(parseFloat(availableQuantity, 10) / parseFloat(item.count, 10))
                                  : 0
                                }
                              </div>
                            </Col>
                          )}
                          <Col xs={isBulk ? 2 : 2}>
                            <TextField
                              autoComplete="off"
                              value={focusedCount[index]
                                ? focusedCount[index].count > 0 ? decimalFixed(focusedCount[index].count) : focusedCount[index].count
                                : decimalFixed(item.count)
                              }
                              fullWidth
                              inputProps={{ step: 'any' }}
                              onBlur={() => {
                                if (!item.count) {
                                  this.onChangePackRow(index, 'count', '0');
                                }
                                this.setFocusedCount(index, false);
                              }}
                              onChange={(e) => {
                                if (parseFloat(e.target.value) <= 0) {
                                  setHotNotification({
                                    autoHideDuration: 3000,
                                    message: 'Count can\'t be less or equal to 0',
                                  });
                                  e.target.value = '';
                                }
                                e.target.value = decimalFixed(e.target.value);
                                this.onChangePackRow(index, 'count', e.target.value);
                                this.setFocusedCount(index, true, e.target.value);
                              }}
                              onFocus={(e) => {
                                if (Number(e.target.value) === 0) {
                                  this.onChangePackRow(index, 'count', '');
                                  this.setFocusedCount(index, true, '');
                                }
                              }}
                              placeholder="0"
                              type="number"
                              varaint="outlined"
                            />
                          </Col>
                          <Col xs={isBulk ? 2 : 3}>
                            {
                              isBulk ? (
                                <div>
                                  { index === 0 ? (
                                    <SelectField
                                      classes={{
                                        item: { root: classes.selectItem },
                                        root: { root: classes.selectRoot },
                                        select: {
                                          select: classes.selectLabel,
                                          selectMenu: classes.selectMenu,
                                        },
                                      }}
                                      disabled={index !== 0 && item.isBulk}
                                      fullWidth
                                      name="uom"
                                      nativeSelect
                                      onChange={e => this.onChangeUOM(index, e)}
                                      options={uomsList}
                                      required
                                      value={item.uom || info.uom}
                                    />
                                  ) : (
                                    <div
                                      style={{
                                        color: 'gray',
                                        fontSize: 12,
                                        whiteSpace: 'nowrap',
                                        marginTop: -60,
                                      }}
                                    >
                                      {item.uom || info.uom}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <SelectField
                                  classes={{
                                    item: { root: classes.selectItem },
                                    root: { root: classes.selectRoot },
                                    select: {
                                      select: classes.selectLabel,
                                      selectMenu: classes.selectMenu,
                                    },
                                  }}
                                  disabled={index !== 0 && item.isBulk}
                                  fullWidth
                                  name="uom"
                                  nativeSelect
                                  onChange={e => this.onChangeUOM(index, e)}
                                  options={uomsList}
                                  required
                                  value={item.uom || info.uom}
                                />
                              )
                            }
                          </Col>
                          <Col xs={isBulk ? 2 : 3}>
                            {
                              <SelectField
                                classes={{
                                  item: { root: classes.selectItem },
                                  root: { root: classes.selectRoot },
                                  select: {
                                    select: classes.selectLabel,
                                    selectMenu: classes.selectMenu,
                                  },
                                }}
                                fullWidth
                                name="pack"
                                nativeSelect
                                onChange={e => this.onChangePackUOM(index, 'pack', e)}
                                options={packsList}
                                required
                                value={item.pack || info.pack}
                              />
                            }
                          </Col>
                          <Col xs={isBulk ? 2 : 2}>
                            <TextField
                              autoComplete="off"
                              fullWidth
                              inputProps={{
                                step: 'any',
                              }}
                              name=""
                              onBlur={({ target: { value } }) => {
                                this.setFocusedPrice(index, false);
                                if (value === '' && parseFloat(item.price) > 0) {
                                  return;
                                }
                                if (!item.price || value === '' || parseFloat(value || 0) < 0) {
                                  this.onChangePackRow(index, 'price', '0');
                                }
                                this.setFocusedPrice(index, false);
                              }}
                              onChange={({ target: { value } }) => {
                                if (parseFloat(value || 0) < 0) {
                                  setHotNotification('Should not be less than 0.');
                                }
                                this.onChangePackRow(index, 'price', value);
                                this.setFocusedPrice(index, true, value);
                              }}
                              onFocus={() => {
                                this.setFocusedPrice(index, true, '');
                              }}
                              onKeyUp={(event) => {
                                event.preventDefault();
                                const inputKeyCode = event.keyCode ? event.keyCode : event.which;
                                if (inputKeyCode != null && (inputKeyCode === 189 || inputKeyCode === 187 || inputKeyCode === 69)) {
                                  setHotNotification('Should not be less than 0.');
                                }
                              }}
                              placeholder="0.00"
                              type="number"
                              value={focusedPrice[index]
                                ? focusedPrice[index].price
                                : parseFloat(item.price || 0).toFixed(2)
                              }
                              varaint="outlined"
                            />
                          </Col>
                        </Row>
                      </Col>
                      {isBulk && (
                        <Col xs="1">
                          <div className="remove-item">
                            <IconButton onClick={() => this.removePackRow(index)}>
                              <Delete color={grey700} />
                            </IconButton>
                          </div>
                        </Col>
                      )}
                    </Row>
                  ) : null;
                })
              }
              {/* {
                !isBulk && (
                  <Row>
                    <Col xs="5">
                      <span className="total-uom" style={{ fontWeight: 600 }}>
                        {formatMessage('Total Number of Packs')}
                      </span>
                      <TextField
                        autoComplete="off"
                        autoFocus
                        value={parseInt(packrows[0].availableQuantity, 10)}
                        fullWidth
                        inputProps={{ step: 'any' }}
                        min={0}
                        name="availableQuantity"
                        onChange={({ target: { value } }) => {
                          if (parseFloat(value || 0) < 0) {
                            setHotNotification('Should not be less than 0.');
                          }
                          this.onChangeAvailableQuantity(value, 0);
                        }}
                        placeholder="0"
                        type="number"
                        varaint="outlined"
                      />
                    </Col>
                  </Row>
                )
              } */}
              {
                isBulk && !isMobile && (
                  <Row>
                    <Col xs="12">
                      <div style={{ color: '#ababab' }}>* FRESHr bulk quantities above recalculate in real-time.</div>
                    </Col>
                  </Row>
                )
              }
              {isRetailPage && (
                <Row style={{ marginBottom: 20 }}>
                  <Col className="available-dates" xs="12">
                    <div className="available-dates-label">
                      {formatMessage('Available Dates')}
                    </div>
                    <div className="date-picker-wrapper">
                      <div className="date-picker" name="date-picker">
                        <Calendar
                          onClickDay={value => this.handlePickDeliveryDate(value)}
                          tileClassName={({ date, view }) => {
                            if (view !== 'month') return '';
                            const { select, enabled } = getPickedAvailableDate(pickedAvailableDates, date);
                            const className = compact([
                              !enabled && 'date disabled',
                              select && 'date select',
                            ]).join(' ');
                            return className;
                          }}
                          tileDisabled={({ date, view }) => {
                            if (view !== 'month') return false;
                            const { enabled } = getPickedAvailableDate(pickedAvailableDates, date);
                            return !enabled;
                          }}
                          calendarType="US"
                          activeStartDate={new Date()}
                          value={new Date()}
                          showNeighboringMonth={false}
                        />
                      </div>
                    </div>
                  </Col>
                </Row>
              )}
              <Row>
                <Col className="form-field" xs="12">
                  <TextField
                    autoComplete="off"
                    value={description}
                    fullWidth
                    label={formatMessage(isFarmer ? 'Description' : 'Enter Vendor name + item description')}
                    name="description"
                    rowsMax={2}
                    variant="outlined"
                    style={{ marginTop: 5 }}
                    onChange={({ target: { value } }) => {
                      this.setState({ description: value, touched: true });
                    }}
                    InputProps={{
                      spellCheck: false,
                      autoCorrect: 'off',
                      autoComplete: 'off',
                    }}
                  />
                </Col>
              </Row>
              <div className="bottom-panel" style={{ marginTop: 10 }}>
                <div className="add-count">
                  {isBulk && (
                    <Fragment>
                      <Button
                        className="no-line-height"
                        classes={{ root: classes.addBtn, label: classes.addBtnLabel }}
                        mini
                        onClick={this.addPackRow}
                        variant="fab"
                        color="primary"
                      >
                        <ContentAdd />
                      </Button>
                      <div className="caption">
                        {formatMessage('ADD COUNT')}
                      </div>
                    </Fragment>
                  )}
                </div>
                {actions}
                {
                  (isEdit) && (
                    <ConfirmAction
                      action={() => {
                        if (isFarmer) {
                          removeStoreItem(info.id); // , { onSuccess: reloadStore }
                        } else {
                          removeGood(info.id); // , { onSuccess: reloadStore }
                        }
                        onRequestClose();
                      }}
                      description={formatMessage('Do you really want to remove this item?')}
                    >
                      <Button className={classes.removeBtn}>
                        {formatMessage('Remove Item')}
                      </Button>
                    </ConfirmAction>
                  )
                }
              </div>
            </Col>
          </Row>
        </form>
      </SmartDialog>
    );
  }
}

/* istanbul ignore next */
const mapStateToProps = state => ({
  ...selectState('goods', 'good')(state, 'good'),
  ...selectState('options', 'packs')(state, 'packs'),
  ...selectState('options', 'tags')(state, 'tags'),
  ...selectState('options', 'uoms')(state, 'uoms'),
  ...selectState('options', 'globals')(state, 'globals'),
  ...selectState('storeItems', 'storeItem')(state, 'storeItem'),
  ...selectState('storeItems', 'storeItems')(state, 'storeItems'),
});

export default compose(
  setDisplayName('EditItemDialog'),
  connect(
    mapStateToProps,
    dispatch => ({
      ...dispatchRestMaker([
        [goodActions, 'good'],
        [storeActions, 'storeItem'],
      ])(dispatch),
      setHotNotification: payload => dispatch(setHotNotification(payload)),
    }),
  ),
  withStyles(styles),
  immutableToJS,
  injectIntl,
)(EditItemDialog);
